import { Container, Box, Typography, useTheme } from '@mui/material';
import React, { type ErrorInfo, type ReactNode } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo?: ErrorInfo | null;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  public state: ErrorBoundaryState = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  public static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error: error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({
      hasError: true,
      error: error,
      errorInfo: errorInfo,
    });
  }

  render() {
    const { hasError, error, errorInfo } = this.state;
    const { children } = this.props;
    return hasError ? <ErrorFallback error={error} errorInfo={errorInfo} /> : children;
  }
}

interface ErrorFallbackProps {
  error: Error | null;
  errorInfo?: ErrorInfo | null;
}

const ErrorFallback: React.FC<ErrorFallbackProps> = () => {
  const theme = useTheme();
  const devTeamEmail = "devcustomer@example.com";
  return (
    <Container data-testid="error-boundary">
      <Box textAlign="center">
        <Typography
          variant="h4"
          fontWeight="bold"
          color={theme.palette.error.main}
          gutterBottom
        >
          Something went wrong
        </Typography>
        <Typography variant="body1" color="textSecondary">
          Please refresh this page in the browser.
        </Typography>
        <Typography variant="body1" color="textSecondary">
          If you continue to see this error, please contact{' '}
          <strong>{devTeamEmail}</strong>.
        </Typography>
      </Box>
    </Container>
  );
};

export default ErrorBoundary;
